import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Pencil,
  Inbox,
  Layers,
  ListChecks,
  Loader2,
  RefreshCw,
  Search,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react'
import { StudioPurchaseModal, type StudioPurchaseMode } from '@/components/ownership/StudioPurchaseModal'
import { StudioOnboardingModal, type StudioOnboardingMode } from '@/components/ownership/StudioOnboardingModal'
import { StudioStateChangeModal, type StudioStateChangeMode } from '@/components/ownership/StudioStateChangeModal'
import {
  cancelStudioSaleOfferFromOriginalCase,
  changeStudioAddressFromOriginalCase,
  closeStudioFromOriginalCase,
  createStudioSaleOffer,
  cancelStudioByIdFromOriginalCase,
  transferStudioToManagementFromOriginalCase,
  resumeStudioFromOriginalCase,
  setStudioServiceStatus,
  suspendStudioFromOriginalCase,
  updateStudioSaleOfferFromOriginalCase,
  type StudioClosureType,
} from '@/services/studioStateChangeService'
import { completeStudioPurchase, planStudioPurchase } from '@/services/studioPurchaseService'
import {
  onboardPartnerNewStudio,
  onboardPartnerStudioPurchase,
  planNewStudioForPartner,
  openStudioFromOriginalCase,
  registerPreopenStudio,
  fetchOnboardingCities,
  fetchCityTimezones,
  type OnboardingCity,
  type OnboardingPersonInput,
} from '@/services/studioOnboardingService'
import { setPartnerStatus, type PartnerStatusMode as PartnerStatusServiceMode } from '@/services/partnerStatusService'
import {
  addPersonToPartnerGroup,
  endPartnerGroupMembership,
} from '@/services/partnerGroupMembershipService'
import { applyDisplayNameChange } from '@/services/partyIdentityService'
import { PartnerStatusModal, type PartnerStatusMode } from '@/components/ownership/PartnerStatusModal'
import {
  PartnerGroupMembershipModal,
  type PartnerGroupMembershipMode,
} from '@/components/ownership/PartnerGroupMembershipModal'
import {
  completeOriginalChangeCase,
  listOriginalChangeCaseStudioCandidates,
  listOriginalChangeCases,
  rejectOriginalChangeCase,
  updateOriginalChangeCaseDetails,
  type OriginalCaseCabinetRow,
  type OriginalCaseStudioCandidate,
  type OriginalCaseStatusFilter,
} from '@/services/originalCaseCabinetService'

const STATUS_LABELS: Record<string, string> = {
  NEW: 'Новый',
  COMPLETED: 'Проведен',
  REJECTED: 'Отклонен',
}

const TARGET_STATUS_LABELS: Record<string, string> = {
  OPEN: 'открыта',
  SUSPENDED: 'приостановлена',
  SERVICE: 'нет продаж',
  CLOSED: 'закрыта',
  CANCELLED: 'отменена',
  SOLD: 'продана',
  BUYING: 'покупка',
  FOR_SALE: 'выставлена на продажу',
  TRANSFERRED: 'передана в УК',
  FORMER: 'бывший',
  TERMINATION: 'на расторжении',
}

const TABS: Array<{ value: OriginalCaseStatusFilter; label: string; icon: LucideIcon }> = [
  { value: 'NEW', label: 'Новые', icon: Inbox },
  { value: 'COMPLETED', label: 'Проведенные', icon: CheckCircle2 },
  { value: 'REJECTED', label: 'Отклоненные', icon: XCircle },
  { value: 'ALL', label: 'Все кейсы', icon: Layers },
]

const REJECTION_REASONS = [
  'Данные уже актуальны',
  'Неверно выбран объект',
  'Неверно выбрано изменение',
  'Недостаточно данных',
  'Другое',
]

type ConductAction =
  | { kind: 'state_change'; mode: StudioStateChangeMode }
  | { kind: 'purchase'; mode: StudioPurchaseMode }
  | { kind: 'onboarding'; mode: StudioOnboardingMode; partnerTarget?: 'new_studio' | 'buy_studio' }
  | { kind: 'partner_status'; mode: PartnerStatusMode }
  | { kind: 'partner_group'; mode: PartnerGroupMembershipMode }

function getConductAction(eventType: string): ConductAction | null {
  const actions: Record<string, ConductAction> = {
    NO_SALES: { kind: 'state_change', mode: 'service_status' },
    SUSPENDED: { kind: 'state_change', mode: 'suspend_studio' },
    CLOSED: { kind: 'state_change', mode: 'close_studio' },
    RESUMED: { kind: 'state_change', mode: 'resume_studio' },
    STATE_CHANGE_CANCEL_STUDIO: { kind: 'state_change', mode: 'cancel_studio' },
    STATE_CHANGE_TRANSFER_TO_MANAGEMENT: { kind: 'state_change', mode: 'transfer_to_management' },
    STATE_CHANGE_CHANGE_ADDRESS: { kind: 'state_change', mode: 'change_address' },
    STATE_CHANGE_SALE_OFFER: { kind: 'state_change', mode: 'sale_offer' },
    STATE_CHANGE_UPDATE_SALE_OFFER: { kind: 'state_change', mode: 'update_sale_offer' },
    SALE_OFFER_CLOSED: { kind: 'state_change', mode: 'cancel_sale_offer' },
    PURCHASE_PLAN: { kind: 'purchase', mode: 'plan' },
    PURCHASE_COMPLETE: { kind: 'purchase', mode: 'complete' },
    ONBOARDING_PARTNER_BUY_STUDIO: { kind: 'onboarding', mode: 'partner', partnerTarget: 'buy_studio' },
    ONBOARDING_PARTNER_NEW_STUDIO: { kind: 'onboarding', mode: 'partner', partnerTarget: 'new_studio' },
    ONBOARDING_STUDIO: { kind: 'onboarding', mode: 'studio' },
    ONBOARDING_REGISTER: { kind: 'onboarding', mode: 'register' },
    ONBOARDING_OPEN: { kind: 'onboarding', mode: 'open' },
    PARTNER_STATUS_FORMER: { kind: 'partner_status', mode: 'former' },
    PARTNER_STATUS_TERMINATION: { kind: 'partner_status', mode: 'termination' },
    PARTNER_GROUP_ADD_PERSON: { kind: 'partner_group', mode: 'add_person' },
    PARTNER_GROUP_END_PERSON: { kind: 'partner_group', mode: 'end_person' },
    PARTNER_GROUP_DISPLAY_NAME: { kind: 'partner_group', mode: 'display_name' },
  }

  return actions[eventType] ?? null
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return 'Не удалось выполнить действие'
}

function formatDate(value: string | null | undefined) {
  if (!value) return '—'
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value
}

function isoDate(value: string | null | undefined) {
  if (!value) return ''
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  return match ? `${match[1]}-${match[2]}-${match[3]}` : value
}

type EventDateFlag = {
  label: string
  description: string
  badgeClassName: string
  hintClassName: string
}

function parseDateOnly(value: string | null | undefined) {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

function getEventDateFlag(value: string | null | undefined): EventDateFlag | null {
  const eventDate = parseDateOnly(value)
  if (!eventDate) return null

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const diffDays = Math.round((today.getTime() - eventDate.getTime()) / 86_400_000)

  if (diffDays < 0) {
    return {
      label: 'Дата в будущем',
      description: 'Дата события еще не наступила',
      badgeClassName: 'bg-sky-100 text-sky-800 ring-sky-200',
      hintClassName: 'border-sky-200 bg-sky-50 text-sky-800',
    }
  }

  if (diffDays > 7) {
    return {
      label: `${diffDays} дн. назад`,
      description: 'От даты события прошло больше 7 дней',
      badgeClassName: 'bg-red-100 text-red-800 ring-red-200',
      hintClassName: 'border-red-200 bg-red-50 text-red-800',
    }
  }

  return null
}

function EventDateValue({ value }: { value: string | null | undefined }) {
  const flag = getEventDateFlag(value)

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span>{formatDate(value)}</span>
      {flag && (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${flag.badgeClassName}`}
          title={flag.description}
        >
          {flag.label}
        </span>
      )}
    </span>
  )
}

function EventDateHint({ value }: { value: string | null | undefined }) {
  const flag = getEventDateFlag(value)
  if (!flag) return null

  return (
    <div className={`mt-2 flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${flag.hintClassName}`}>
      <AlertCircle size={15} className="mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold">{flag.label}</div>
        <div>{flag.description}</div>
      </div>
    </div>
  )
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—'
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/)
  return match ? `${match[3]}.${match[2]}.${match[1]} ${match[4]}:${match[5]}` : value
}

function dateAtTen(value: string): string {
  return `${value} 10:00:00`
}

function dateAtNoon(value: string): string {
  return `${value} 12:00:00`
}

function dateAtTwo(value: string): string {
  return `${value} 14:00:00`
}

function dateAtThree(value: string): string {
  return `${value} 15:00:00`
}

function statusLabel(value: string | null | undefined) {
  if (!value) return '—'
  return TARGET_STATUS_LABELS[value] ?? value
}

function statusBadgeClass(status: string) {
  if (status === 'COMPLETED') return 'bg-emerald-100 text-emerald-700 ring-emerald-200'
  if (status === 'REJECTED') return 'bg-red-100 text-red-700 ring-red-200'
  return 'bg-blue-100 text-blue-700 ring-blue-200'
}

function changeParts(row: OriginalCaseCabinetRow) {
  const from = row.is_bulk ? 'Выбранные студии' : currentValue(row)
  const to = row.target_status ? statusLabel(row.target_status) : row.event_label
  return { from, to }
}

function primaryStudioLine(row: OriginalCaseCabinetRow) {
  const studio = row.studios[0]
  if (!studio) return row.object_display_name || '—'
  const number = studio.studio_number ?? studio.studio_internal_id
  return `Студия ${number}${studio.city_name ? ` · ${studio.city_name}` : ''}`
}

function partnerLine(row: OriginalCaseCabinetRow) {
  if (row.is_bulk) return `${row.studio_count.toLocaleString('ru-RU')} выбранных студий`
  return row.studios[0]?.owner_persons_fio || 'Франчайзи не указан'
}

function objectLabel(row: OriginalCaseCabinetRow) {
  if (row.is_bulk) return `${row.studio_count.toLocaleString('ru-RU')} студий`
  return row.object_display_name || row.studios[0]?.studio_number?.toString() || `Кейс #${row.id}`
}

function currentValue(row: OriginalCaseCabinetRow) {
  const status = row.studios[0]?.status_code
  return row.is_bulk ? 'Список студий' : statusLabel(status)
}

function payloadText(row: OriginalCaseCabinetRow, key: string) {
  const value = row.payload?.[key]
  return typeof value === 'string' ? value : ''
}

function payloadNumber(row: OriginalCaseCabinetRow, key: string) {
  const value = row.payload?.[key]
  const numberValue = typeof value === 'number' ? value : Number(String(value ?? '').replace(/\s/g, '').trim())
  return Number.isFinite(numberValue) ? numberValue : null
}

function payloadBoolean(row: OriginalCaseCabinetRow, key: string) {
  return row.payload?.[key] === true
}

function onboardingPersonsFromPayload(row: OriginalCaseCabinetRow): OnboardingPersonInput[] {
  const raw = row.payload?.partner_group_persons
  if (!Array.isArray(raw)) return []
  return raw
    .filter((person): person is Record<string, unknown> => person !== null && typeof person === 'object')
    .map(person => {
      const gender: OnboardingPersonInput['gender'] = person.gender === 'м' || person.gender === 'ж' ? person.gender : null
      return {
        first_name: typeof person.first_name === 'string' ? person.first_name.trim() : '',
        last_name: typeof person.last_name === 'string' ? person.last_name.trim() : '',
        gender,
      }
    })
    .filter(person => person.first_name && person.last_name)
}

function hasAttachments(row: OriginalCaseCabinetRow) {
  return Array.isArray(row.attachments) && row.attachments.length > 0
}

function closureTypeFromPayload(row: OriginalCaseCabinetRow): StudioClosureType {
  const value = row.payload?.closure_type
  return value === 'REBRANDING' || value === 'UNAPPROVED' ? value : 'STANDARD'
}

function conductNote(row: OriginalCaseCabinetRow): string | null {
  const parts = [
    payloadText(row, 'happened'),
    row.comment ?? '',
  ].map(part => part.trim()).filter(Boolean)
  return parts.length > 0 ? parts.join('\n\n') : null
}

function isDirectStateConductCase(row: OriginalCaseCabinetRow): boolean {
  return row.event_type === 'NO_SALES'
    || row.event_type === 'SUSPENDED'
    || row.event_type === 'CLOSED'
    || row.event_type === 'RESUMED'
}

function isDirectStructuredConductCase(row: OriginalCaseCabinetRow): boolean {
  return isDirectStateConductCase(row)
    || row.event_type === 'ONBOARDING_PARTNER_NEW_STUDIO'
    || row.event_type === 'ONBOARDING_PARTNER_BUY_STUDIO'
    || row.event_type === 'ONBOARDING_STUDIO'
    || row.event_type === 'ONBOARDING_REGISTER'
    || row.event_type === 'ONBOARDING_OPEN'
    || row.event_type === 'STATE_CHANGE_CANCEL_STUDIO'
    || row.event_type === 'STATE_CHANGE_TRANSFER_TO_MANAGEMENT'
    || row.event_type === 'STATE_CHANGE_SALE_OFFER'
    || row.event_type === 'STATE_CHANGE_UPDATE_SALE_OFFER'
    || row.event_type === 'SALE_OFFER_CLOSED'
    || row.event_type === 'STATE_CHANGE_CHANGE_ADDRESS'
    || row.event_type === 'PURCHASE_PLAN'
    || row.event_type === 'PURCHASE_COMPLETE'
    || row.event_type.startsWith('PARTNER_STATUS_')
    || row.event_type === 'PARTNER_GROUP_ADD_PERSON'
    || row.event_type === 'PARTNER_GROUP_END_PERSON'
}

function supportsBulkScopeEdit(row: OriginalCaseCabinetRow): boolean {
  return row.event_type === 'NO_SALES'
    || row.event_type === 'SUSPENDED'
    || row.event_type === 'CLOSED'
    || row.event_type === 'RESUMED'
}

function partnerStatusModeFromEventType(eventType: string): PartnerStatusServiceMode | null {
  if (eventType === 'PARTNER_STATUS_FORMER') return 'former'
  if (eventType === 'PARTNER_STATUS_TERMINATION') return 'termination'
  return null
}

function payloadDisplayRows(row: OriginalCaseCabinetRow): Array<{ label: string; value: string }> {
  const rows: Array<{ label: string; value: string }> = []
  const add = (label: string, value: unknown) => {
    if (value === null || value === undefined || value === '') return
    rows.push({ label, value: typeof value === 'boolean' ? (value ? 'Да' : 'Нет') : String(value) })
  }

  if (row.event_type === 'ONBOARDING_PARTNER_NEW_STUDIO' || row.event_type === 'ONBOARDING_STUDIO') {
    add('Город', row.payload.city_name)
    add('Страна', row.payload.country)
    add('Население, тыс. чел.', row.payload.population_thousands)
    add('Группа населения', row.payload.city_population_group)
    add('Земельная группа', row.payload.city_land_group)
    add('Адрес или ориентир', row.payload.planned_address)
  }
  if (row.event_type === 'ONBOARDING_REGISTER') {
    add('Адрес студии', row.payload.register_address)
    add('Номер студии', row.payload.register_studio_number)
  }

  if (row.event_type === 'CLOSED') add('Тип закрытия', closureTypeFromPayload(row))
  if (row.event_type === 'STATE_CHANGE_SALE_OFFER' || row.event_type === 'STATE_CHANGE_UPDATE_SALE_OFFER') {
    add('Цена оффера', row.payload.offer_price)
    add('Валюта', row.payload.offer_currency)
    add('Торг разрешен', row.payload.offer_is_bargain_allowed)
  }
  if (row.event_type === 'STATE_CHANGE_CHANGE_ADDRESS') add('Новый адрес', row.payload.new_address)
  if (row.event_type === 'PURCHASE_PLAN' || row.event_type === 'PURCHASE_COMPLETE') {
    add('Покупатель', row.payload.buyer_partner_number)
    add('Продавец уходит из сети', row.payload.seller_exits_network)
  }
  if (row.event_type.startsWith('PARTNER_STATUS_') || row.event_type.startsWith('PARTNER_GROUP_')) {
    add('Франчайзи', row.payload.partner_number)
  }
  if (row.event_type === 'PARTNER_GROUP_ADD_PERSON' || row.event_type === 'PARTNER_GROUP_END_PERSON') {
    add('Имя', row.payload.person_first_name)
    add('Фамилия', row.payload.person_last_name)
  }
  if (row.event_type === 'PARTNER_GROUP_ADD_PERSON') {
    add('Пол', row.payload.person_gender)
    add('Telegram', row.payload.person_tg_username)
  }
  if (row.event_type === 'PARTNER_GROUP_DISPLAY_NAME') {
    add('ID персоны', row.payload.person_id)
    add('Форма имени', row.payload.display_name || 'паспортное ФИО')
  }

  return rows
}

type CaseDrawerProps = {
  row: OriginalCaseCabinetRow
  onClose: () => void
  onReject: () => void
  onConduct: () => void
  onEdit: () => void
  actionError: string | null
  conducting: boolean
}

function CaseDrawer({ row, onClose, onReject, onConduct, onEdit, actionError, conducting }: CaseDrawerProps) {
  const conductAction = getConductAction(row.event_type)
  const change = changeParts(row)
  const payloadRows = payloadDisplayRows(row)
  const processedClass = row.status === 'COMPLETED'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
    : 'border-red-200 bg-red-50 text-red-800'

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <aside
        className="flex h-full w-full max-w-[600px] flex-col border-l border-slate-200 bg-card shadow-xl"
        onClick={event => event.stopPropagation()}
      >
        <div className="shrink-0 border-b border-slate-200 bg-card px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-slate-900">Кейс #{row.id}</h2>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${statusBadgeClass(row.status)}`}>
                  {STATUS_LABELS[row.status] ?? row.status}
                </span>
              </div>
              <div className="mt-2 flex min-w-0 items-center gap-2 text-base font-semibold text-slate-950">
                <span className="truncate">{change.from}</span>
                <ArrowRight size={17} className="shrink-0 text-amber-600" />
                <span className="truncate text-amber-700">{change.to}</span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                <span>{row.event_label}</span>
                <span>·</span>
                <span>дата события</span>
                <EventDateValue value={row.changed_at} />
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              aria-label="Закрыть"
              title="Закрыть"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-sm">
          <div className="space-y-4 pb-4">
            {actionError && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-red-700">
                <AlertCircle size={17} className="mt-0.5 shrink-0" />
                <div>{actionError}</div>
              </div>
            )}

            <section className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
              <div className="font-semibold text-slate-900">{objectLabel(row)}</div>
              <div className="mt-1 text-sm text-slate-600">
                {row.is_bulk ? `${row.studio_count.toLocaleString('ru-RU')} студий` : primaryStudioLine(row)}
              </div>
              <div className="mt-0.5 text-xs text-slate-500">{partnerLine(row)}</div>
              {row.is_bulk && (
                <div className="mt-3 max-h-32 overflow-y-auto border-t border-slate-200 pt-2 text-xs text-slate-700">
                  {row.studios.map(studio => (
                    <div key={studio.studio_internal_id} className="py-0.5">
                      №{studio.studio_number ?? studio.studio_internal_id}
                      {studio.city_name ? ` · ${studio.city_name}` : ''}
                      {studio.owner_persons_fio ? ` · ${studio.owner_persons_fio}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <div className="grid gap-3 sm:grid-cols-2">
              <section className="rounded-md border border-slate-200 bg-card px-3 py-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Сейчас в базе</div>
                <div className="text-lg font-semibold text-slate-900">{currentValue(row)}</div>
                <div className="mt-1 text-xs text-slate-500">
                  {row.is_bulk ? 'Текущий снимок выбранных студий' : row.object_display_name || primaryStudioLine(row)}
                </div>
              </section>

              <section className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-700">Передал информатор</div>
                <div className="text-lg font-semibold text-slate-950">{change.to}</div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-amber-800">
                  <span>Дата события:</span>
                  <EventDateValue value={row.changed_at} />
                </div>
              </section>
            </div>

            <section className="rounded-md border border-slate-200 bg-card px-3 py-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Детали</div>
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-slate-500">Что произошло</div>
                  <div className="mt-0.5 whitespace-pre-wrap text-slate-900">{payloadText(row, 'happened') || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Комментарий</div>
                  <div className="mt-0.5 whitespace-pre-wrap text-slate-900">{row.comment || '—'}</div>
                </div>
                <div className="grid gap-2 text-xs text-slate-500 sm:grid-cols-2">
                  <div>Передал: <span className="text-slate-700">{row.created_by_name || '—'}</span></div>
                  <div>Кейс создан: <span className="text-slate-700">{formatDateTime(row.created_at)}</span></div>
                </div>
              </div>
            </section>

            {payloadRows.length > 0 && (
              <section className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-700">Переданные поля</div>
                <div className="grid gap-2 text-sm">
                  {payloadRows.map(item => (
                    <div key={item.label} className="grid grid-cols-[150px_1fr] gap-2">
                      <span className="text-amber-800">{item.label}</span>
                      <span className="min-w-0 whitespace-pre-wrap font-medium text-slate-950">{item.value}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {hasAttachments(row) && (
              <section className="rounded-md border border-slate-200 bg-card px-3 py-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Вложения</div>
                <div className="space-y-1">
                  {row.attachments.map((attachment, index) => {
                    const label = String(attachment.label ?? attachment.url ?? `Вложение ${index + 1}`)
                    const url = typeof attachment.url === 'string' ? attachment.url : null
                    return url ? (
                      <a key={`${label}:${index}`} href={url} target="_blank" rel="noreferrer" className="block text-sm text-amber-700 hover:underline">
                        {label}
                      </a>
                    ) : (
                      <div key={`${label}:${index}`} className="text-sm text-slate-700">{label}</div>
                    )
                  })}
                </div>
              </section>
            )}

            {row.status !== 'NEW' && (
              <section className={`rounded-md border px-3 py-3 ${processedClass}`}>
                <div className="flex items-start gap-2">
                  {row.status === 'COMPLETED' ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <XCircle size={18} className="mt-0.5 shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-semibold">{STATUS_LABELS[row.status] ?? row.status}</div>
                    {row.status === 'COMPLETED' ? (
                      <div className="mt-1 text-sm">
                        Провел: {row.completed_by_name || '—'} · дата проведения {formatDateTime(row.completed_at)}
                      </div>
                    ) : (
                      <div className="mt-1 space-y-0.5 text-sm">
                        <div>Отклонил: {row.rejected_by_name || '—'} · дата отклонения {formatDateTime(row.rejected_at)}</div>
                        <div>Причина: {row.rejection_reason || '—'}</div>
                        {row.rejection_comment && <div>Комментарий: {row.rejection_comment}</div>}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}
          </div>
        </div>

        {row.status === 'NEW' && (
          <div className="shrink-0 border-t border-slate-200 bg-card px-5 py-3">
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onReject}
                disabled={conducting}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Отклонить
              </button>
              <button
                type="button"
                onClick={onConduct}
                disabled={conducting || (!conductAction && !isDirectStructuredConductCase(row))}
                title={
                  isDirectStructuredConductCase(row)
                    ? 'Провести изменение сразу по данным кейса'
                    : conductAction
                      ? 'Открыть существующую форму проведения'
                      : 'Для этого типа кейса нет связанной формы проведения'
                }
                className="rounded-md bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-300 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
              >
                {conducting ? 'Проводим...' : 'Провести'}
              </button>
              {row.event_type !== 'PARTNER_GROUP_DISPLAY_NAME' && <button
                type="button"
                onClick={onEdit}
                disabled={conducting}
                className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Pencil size={14} />
                Редактировать
              </button>}
            </div>
          </div>
        )}
      </aside>
    </div>
  )
}

type EditCaseScopeDialogProps = {
  row: OriginalCaseCabinetRow
  onClose: () => void
  onSaved: () => void
}

function candidateLabel(candidate: OriginalCaseStudioCandidate) {
  const number = candidate.studio_number ?? candidate.studio_internal_id
  return `№${number} · ${candidate.city_name || 'город не указан'}`
}

function candidateMeta(candidate: OriginalCaseStudioCandidate) {
  return [
    candidate.owner_partner_number ? `франчайзи №${candidate.owner_partner_number}` : null,
    candidate.owner_persons_fio || null,
    candidate.status_code || null,
    candidate.address || null,
  ].filter(Boolean).join(' · ')
}

const EDITABLE_CASE_FIELDS: Record<string, string> = {
  happened: 'Описание изменения',
  city_id: 'Город',
  city_name: 'Город',
  iana_timezone: 'Часовой пояс нового города',
  country: 'Страна',
  population_thousands: 'Население, тыс. чел.',
  city_population_group: 'Группа населения',
  city_land_group: 'Земельная группа',
  planned_address: 'Адрес или ориентир',
  register_address: 'Адрес студии',
  register_studio_number: 'Номер студии',
  closure_type: 'Тип закрытия',
  offer_price: 'Цена оффера',
  offer_currency: 'Валюта',
  offer_is_bargain_allowed: 'Торг разрешен',
  new_address: 'Новый адрес',
  buyer_partner_number: 'Номер франчайзи-покупателя',
  seller_exits_network: 'Продавец уходит из сети',
  new_franchisee_first_name: 'Имя нового франчайзи',
  new_franchisee_last_name: 'Фамилия нового франчайзи',
  new_franchisee_gender: 'Пол нового франчайзи',
  new_franchisee_tg_username: 'Telegram нового франчайзи',
  partner_group_persons: 'Участники франчайзи',
  partner_number: 'Номер франчайзи',
  person_first_name: 'Имя участника',
  person_last_name: 'Фамилия участника',
  person_gender: 'Пол участника',
  person_tg_username: 'Telegram участника',
  display_name: 'Отображаемое имя',
}

const EDITABLE_CASE_NUMBER_FIELDS = new Set([
  'population_thousands', 'city_population_group', 'city_land_group', 'register_studio_number',
  'offer_price', 'buyer_partner_number', 'partner_number',
])
const EDITABLE_CASE_BOOLEAN_FIELDS = new Set(['offer_is_bargain_allowed', 'seller_exits_network'])

function EditCaseScopeDialog({ row, onClose, onSaved }: EditCaseScopeDialogProps) {
  const allowMultiple = supportsBulkScopeEdit(row)
  const [changedAt, setChangedAt] = useState(isoDate(row.changed_at))
  const [targetStatus, setTargetStatus] = useState(row.target_status ?? String(row.payload.target_status ?? ''))
  const [payload, setPayload] = useState<Record<string, unknown>>(() => ({
    ...row.payload,
    ...(row.event_type === 'CLOSED' ? { closure_type: closureTypeFromPayload(row) } : {}),
    ...(['ONBOARDING_PARTNER_NEW_STUDIO', 'ONBOARDING_STUDIO'].includes(row.event_type)
      ? {
          city_name: row.payload.city_name ?? '',
          country: row.payload.country ?? '',
          iana_timezone: row.payload.iana_timezone || 'Europe/Moscow',
        }
      : {}),
  }))
  const [cities, setCities] = useState<OnboardingCity[]>([])
  const [timezones, setTimezones] = useState<string[]>([])
  const [comment, setComment] = useState(row.comment ?? '')
  const [attachments, setAttachments] = useState<Array<Record<string, unknown>>>(() => row.attachments ?? [])
  const [query, setQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<number[]>(() => row.studios.map(studio => studio.studio_internal_id))
  const [candidates, setCandidates] = useState<OriginalCaseStudioCandidate[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchOnboardingCities().then(items => {
      if (!cancelled) setCities(items)
    }).catch(() => {
      if (!cancelled) setCities([])
    })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    fetchCityTimezones().then(items => {
      if (!cancelled) setTimezones(items)
    }).catch(() => {
      if (!cancelled) setTimezones(['Europe/Moscow'])
    })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    const timeout = window.setTimeout(() => {
      setLoading(true)
      listOriginalChangeCaseStudioCandidates(query)
        .then(rows => {
          if (cancelled) return
          const selectedAsCandidates: OriginalCaseStudioCandidate[] = row.studios.map(studio => ({
            studio_internal_id: studio.studio_internal_id,
            studio_number: studio.studio_number,
            city_name: studio.city_name,
            country: studio.country,
            address: studio.address,
            status_code: studio.status_code,
            owner_partner_number: studio.owner_partner_number,
            owner_persons_fio: studio.owner_persons_fio,
          }))
          const byId = new Map<number, OriginalCaseStudioCandidate>()
          selectedAsCandidates.forEach(item => byId.set(item.studio_internal_id, item))
          rows.forEach(item => byId.set(item.studio_internal_id, item))
          setCandidates(Array.from(byId.values()))
          setError(null)
        })
        .catch(loadError => {
          if (!cancelled) setError(getErrorMessage(loadError))
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, 250)

    return () => {
      cancelled = true
      window.clearTimeout(timeout)
    }
  }, [query, row.studios])

  const toggleCandidate = (candidate: OriginalCaseStudioCandidate) => {
    setSelectedIds(current => {
      if (!allowMultiple) return [candidate.studio_internal_id]
      return current.includes(candidate.studio_internal_id)
        ? current.filter(id => id !== candidate.studio_internal_id)
        : [...current, candidate.studio_internal_id]
    })
  }

  const updateGroupPerson = (index: number, key: 'first_name' | 'last_name' | 'gender' | 'tg_username', value: string) => {
    setPayload(current => {
      const people = Array.isArray(current.partner_group_persons) ? current.partner_group_persons : []
      return {
        ...current,
        partner_group_persons: people.map((person, personIndex) =>
          personIndex === index && person !== null && typeof person === 'object'
            ? { ...(person as Record<string, unknown>), [key]: value || null }
            : person
        ),
      }
    })
  }

  const canSave = !saving && Boolean(changedAt) && selectedIds.length > 0
    && (allowMultiple || selectedIds.length === 1)
  const canEditCity = row.event_type === 'ONBOARDING_PARTNER_NEW_STUDIO' || row.event_type === 'ONBOARDING_STUDIO'

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    setError(null)
    try {
      const editedPayload = { ...payload }
      const people = Array.isArray(payload.partner_group_persons)
        ? payload.partner_group_persons.filter((person): person is Record<string, unknown> => person !== null && typeof person === 'object')
        : []
      if (people.some(person => !String(person.first_name ?? '').trim() || !String(person.last_name ?? '').trim())) {
        throw new Error('Укажите имя и фамилию каждого участника франчайзи')
      }
      if (people.length > 0 && 'new_franchisee_first_name' in editedPayload) {
        const firstPerson = people[0]
        editedPayload.new_franchisee_first_name = firstPerson.first_name
        editedPayload.new_franchisee_last_name = firstPerson.last_name
        editedPayload.new_franchisee_gender = firstPerson.gender ?? null
        editedPayload.new_franchisee_tg_username = firstPerson.tg_username ?? null
      }
      await updateOriginalChangeCaseDetails({
        caseId: row.id,
        changedAt,
        targetStatus: targetStatus.trim() || null,
        studioInternalIds: selectedIds,
        payload: editedPayload,
        comment: comment.trim() || null,
        attachments,
      })
      onSaved()
    } catch (saveError) {
      setError(getErrorMessage(saveError))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-slate-200 bg-card shadow-xl">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <div className="font-semibold text-slate-900">Редактировать кейс #{row.id}</div>
            <div className="mt-0.5 text-sm text-slate-500">
              Можно изменить переданные данные, комментарий, дату и {allowMultiple ? 'набор студий' : 'студию'}.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle size={17} className="mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-slate-700">Дата события</span>
            <input
              type="date"
              value={changedAt}
              onChange={event => setChangedAt(event.target.value)}
              className="h-10 w-full rounded-md border border-slate-200 bg-card px-3 text-sm outline-none focus:ring-1 focus:ring-amber-400"
            />
            <EventDateHint value={changedAt} />
          </label>

          {(row.target_status || row.payload.target_status) && (
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Целевой статус</span>
              <select value={targetStatus} onChange={event => setTargetStatus(event.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-card px-3 text-sm">
                <option value="">Не указан</option>
                {Object.entries(TARGET_STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          )}

          <section className="space-y-3 rounded-md border border-slate-200 p-3">
            <div className="text-sm font-semibold text-slate-800">Данные кейса</div>
            {Object.entries(payload).filter(([key, value]) => EDITABLE_CASE_FIELDS[key]
              && !(key === 'partner_number' && (row.event_type.startsWith('PARTNER_STATUS_') || row.event_type.startsWith('PARTNER_GROUP_')))
              && !(key.startsWith('new_franchisee_') && Array.isArray(payload.partner_group_persons) && payload.partner_group_persons.length > 0)
              && (
              value !== null && value !== ''
              || key === 'closure_type' && row.event_type === 'CLOSED'
              || key === 'city_id' && canEditCity
              || ['city_name', 'country', 'iana_timezone'].includes(key) && canEditCity && payload.city_id == null
            )).map(([key, value]) => {
              const label = EDITABLE_CASE_FIELDS[key]
              if (key === 'closure_type' && row.event_type === 'CLOSED') {
                return <label key={key} className="block space-y-1"><span className="text-xs font-medium text-slate-600">{label}</span><select value={String(value ?? 'STANDARD')} onChange={event => setPayload(current => ({ ...current, [key]: event.target.value }))} className="h-10 w-full rounded-md border border-slate-200 bg-card px-3 text-sm"><option value="STANDARD">Обычное закрытие</option><option value="REBRANDING">Ребрендинг</option><option value="UNAPPROVED">Не согласовано</option></select></label>
              }
              if (key === 'city_id' && canEditCity) {
                const cityId = value == null ? null : Number(value)
                const cityOptions = cities.some(city => city.city_id === cityId)
                  ? cities
                  : cityId === null ? cities : [{ city_id: cityId, city_name: `Город #${cityId}`, country: '', studio_count: 0 }, ...cities]
                return <label key={key} className="block space-y-1"><span className="text-xs font-medium text-slate-600">Выберите город</span><select value={cityId == null ? '' : String(cityId)} onChange={event => setPayload(current => ({ ...current, city_id: event.target.value ? Number(event.target.value) : null, ...(event.target.value ? { city_name: null, country: null } : { city_name: '', country: '', iana_timezone: 'Europe/Moscow' }) }))} className="h-10 w-full rounded-md border border-slate-200 bg-card px-3 text-sm"><option value="">Новый город (ввести данные)</option>{cityOptions.map(city => <option key={city.city_id} value={city.city_id}>{city.city_name}{city.country ? `, ${city.country}` : ''}</option>)}</select></label>
              }
              if (key === 'iana_timezone' && canEditCity && payload.city_id == null) {
                const timezone = String(value || 'Europe/Moscow')
                const timezoneOptions = timezones.includes(timezone) ? timezones : [timezone, ...timezones]
                return <label key={key} className="block space-y-1"><span className="text-xs font-medium text-slate-600">{label}</span><select value={timezone} onChange={event => setPayload(current => ({ ...current, [key]: event.target.value }))} className="h-10 w-full rounded-md border border-slate-200 bg-card px-3 text-sm">{timezoneOptions.map(item => <option key={item} value={item}>{item}</option>)}</select></label>
              }
              if (key === 'partner_group_persons' && Array.isArray(value)) {
                return <fieldset key={key} className="space-y-2"><legend className="text-xs font-medium text-slate-600">{label}</legend>{value.map((person, index) => {
                  const item = person !== null && typeof person === 'object' ? person as Record<string, unknown> : {}
                  return <div key={index} className="grid grid-cols-1 gap-2 rounded-md bg-slate-50 p-2 sm:grid-cols-2">
                    <input aria-label={`Имя участника ${index + 1}`} value={String(item.first_name ?? '')} onChange={event => updateGroupPerson(index, 'first_name', event.target.value)} placeholder="Имя" className="h-9 rounded-md border border-slate-200 bg-card px-2 text-sm" />
                    <input aria-label={`Фамилия участника ${index + 1}`} value={String(item.last_name ?? '')} onChange={event => updateGroupPerson(index, 'last_name', event.target.value)} placeholder="Фамилия" className="h-9 rounded-md border border-slate-200 bg-card px-2 text-sm" />
                    <select aria-label={`Пол участника ${index + 1}`} value={String(item.gender ?? '')} onChange={event => updateGroupPerson(index, 'gender', event.target.value)} className="h-9 rounded-md border border-slate-200 bg-card px-2 text-sm"><option value="">Пол не указан</option><option value="ж">Женский</option><option value="м">Мужской</option></select>
                    <input aria-label={`Telegram участника ${index + 1}`} value={String(item.tg_username ?? '')} onChange={event => updateGroupPerson(index, 'tg_username', event.target.value)} placeholder="Telegram" className="h-9 rounded-md border border-slate-200 bg-card px-2 text-sm" />
                  </div>
                })}</fieldset>
              }
              if (EDITABLE_CASE_BOOLEAN_FIELDS.has(key)) return <label key={key} className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={value === true} onChange={event => setPayload(current => ({ ...current, [key]: event.target.checked }))} className="h-4 w-4" />{label}</label>
              if (EDITABLE_CASE_NUMBER_FIELDS.has(key)) return <label key={key} className="block space-y-1"><span className="text-xs font-medium text-slate-600">{label}</span><input type="number" value={typeof value === 'number' ? value : ''} onChange={event => setPayload(current => ({ ...current, [key]: event.target.value === '' ? null : Number(event.target.value) }))} className="h-10 w-full rounded-md border border-slate-200 bg-card px-3 text-sm" /></label>
              return <label key={key} className="block space-y-1"><span className="text-xs font-medium capitalize text-slate-600">{label}</span><input value={value == null ? '' : String(value)} onChange={event => setPayload(current => ({ ...current, [key]: event.target.value }))} className="h-10 w-full rounded-md border border-slate-200 bg-card px-3 text-sm" /></label>
            })}
            <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">Комментарий</span><textarea value={comment} onChange={event => setComment(event.target.value)} rows={3} className="w-full rounded-md border border-slate-200 bg-card px-3 py-2 text-sm" /></label>
            {attachments.length > 0 && <fieldset className="space-y-2"><legend className="text-xs font-medium text-slate-600">Вложения</legend>{attachments.map((attachment, index) => <div key={index} className="grid grid-cols-1 gap-2 rounded-md bg-slate-50 p-2 sm:grid-cols-2"><input aria-label={`Название вложения ${index + 1}`} value={String(attachment.label ?? '')} onChange={event => setAttachments(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, label: event.target.value } : item))} placeholder="Название" className="h-9 rounded-md border border-slate-200 bg-card px-2 text-sm" /><input aria-label={`Ссылка на вложение ${index + 1}`} value={String(attachment.url ?? '')} onChange={event => setAttachments(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, url: event.target.value } : item))} placeholder="Ссылка" className="h-9 rounded-md border border-slate-200 bg-card px-2 text-sm" /></div>)}</fieldset>}
            <div className="text-xs text-slate-500">Состав студий редактируется отдельным списком ниже.</div>
          </section>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-medium text-slate-700">
                {allowMultiple ? 'Студии' : 'Студия'}
              </div>
              <div className="text-xs text-slate-500">Выбрано: {selectedIds.length}</div>
            </div>
            <div className="flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-card px-3">
              <Search size={15} className="shrink-0 text-slate-400" />
              <input
                value={query}
                onChange={event => setQuery(event.target.value)}
                className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                placeholder="Номер, город, адрес, франчайзи"
              />
              {loading && <Loader2 size={15} className="animate-spin text-slate-400" />}
            </div>

            <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200">
              {candidates.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-slate-500">
                  {loading ? 'Ищем студии' : 'Студии не найдены'}
                </div>
              ) : (
                candidates.map(candidate => {
                  const checked = selectedIds.includes(candidate.studio_internal_id)
                  return (
                    <button
                      key={candidate.studio_internal_id}
                      type="button"
                      onClick={() => toggleCandidate(candidate)}
                      className={`flex w-full items-start gap-3 border-b border-slate-100 px-3 py-2 text-left last:border-b-0 hover:bg-amber-50 ${
                        checked ? 'bg-amber-50' : ''
                      }`}
                    >
                      <input
                        type={allowMultiple ? 'checkbox' : 'radio'}
                        checked={checked}
                        onChange={() => toggleCandidate(candidate)}
                        className="mt-1 h-4 w-4 shrink-0"
                        onClick={event => event.stopPropagation()}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-900">{candidateLabel(candidate)}</span>
                        <span className="mt-0.5 block text-xs text-slate-500">{candidateMeta(candidate) || 'без дополнительной информации'}</span>
                      </span>
                    </button>
                  )
                })
              )}
            </div>
            {!allowMultiple && selectedIds.length > 1 && (
              <div className="text-xs text-red-600">Для этого типа кейса можно выбрать только одну студию.</div>
            )}
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={!canSave}
            className="inline-flex items-center gap-2 rounded-md bg-amber-400 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-300 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
          >
            {saving && <Loader2 size={15} className="animate-spin" />}
            Сохранить
          </button>
        </div>
      </div>
    </div>
  )
}

export function OriginalCasesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<OriginalCaseStatusFilter>('NEW')
  const [rows, setRows] = useState<OriginalCaseCabinetRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const value = Number(searchParams.get('case'))
    return Number.isFinite(value) && value > 0 ? value : null
  })
  const [actionError, setActionError] = useState<string | null>(null)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [rejectSubmitting, setRejectSubmitting] = useState(false)
  const [rejectionReason, setRejectionReason] = useState(REJECTION_REASONS[0])
  const [rejectionComment, setRejectionComment] = useState('')
  const [conductAction, setConductAction] = useState<ConductAction | null>(null)
  const [caseCompletingAfterConduct, setCaseCompletingAfterConduct] = useState<number | null>(null)
  const [conductSubmittingId, setConductSubmittingId] = useState<number | null>(null)

  const selectedCase = useMemo(
    () => rows.find(row => row.id === selectedId) ?? null,
    [rows, selectedId]
  )
  const statusCounts = useMemo(() => ({
    NEW: rows.filter(row => row.status === 'NEW').length,
    COMPLETED: rows.filter(row => row.status === 'COMPLETED').length,
    REJECTED: rows.filter(row => row.status === 'REJECTED').length,
    ALL: rows.length,
  }), [rows])
  const visibleRows = useMemo(
    () => activeTab === 'ALL' ? rows : rows.filter(row => row.status === activeTab),
    [activeTab, rows]
  )

  const loadRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const loaded = await listOriginalChangeCases('ALL')
      setRows(loaded)
    } catch (loadError) {
      setError(getErrorMessage(loadError))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadRows()
  }, [loadRows])

  const openCase = (row: OriginalCaseCabinetRow) => {
    setSelectedId(row.id)
    setActionError(null)
    const next = new URLSearchParams(searchParams)
    next.set('case', String(row.id))
    setSearchParams(next, { replace: true })
  }

  const closeCase = () => {
    setSelectedId(null)
    setActionError(null)
    setEditDialogOpen(false)
    const next = new URLSearchParams(searchParams)
    next.delete('case')
    setSearchParams(next, { replace: true })
  }

  const changeTab = (nextTab: OriginalCaseStatusFilter) => {
    setActiveTab(nextTab)
    setSelectedId(null)
    setActionError(null)
    setEditDialogOpen(false)
    const next = new URLSearchParams(searchParams)
    next.delete('case')
    setSearchParams(next, { replace: true })
  }

  const handleReject = async () => {
    if (!selectedCase || rejectSubmitting) return
    setRejectSubmitting(true)
    setActionError(null)
    try {
      await rejectOriginalChangeCase({
        caseId: selectedCase.id,
        reason: rejectionReason,
        comment: rejectionComment,
      })
      setRejectDialogOpen(false)
      setRejectionComment('')
      await loadRows()
    } catch (rejectError) {
      setActionError(getErrorMessage(rejectError))
    } finally {
      setRejectSubmitting(false)
    }
  }

  const applyDirectStructuredCase = async (row: OriginalCaseCabinetRow) => {
    if (!isDirectStructuredConductCase(row)) return false
    if (row.event_type === 'ONBOARDING_PARTNER_NEW_STUDIO' || row.event_type === 'ONBOARDING_STUDIO' || row.event_type === 'ONBOARDING_PARTNER_BUY_STUDIO') {
      const persons = onboardingPersonsFromPayload(row)
      const cityName = payloadText(row, 'city_name')
      const country = payloadText(row, 'country')
      const note = conductNote(row)
      if (row.event_type === 'ONBOARDING_PARTNER_NEW_STUDIO') {
        await onboardPartnerNewStudio({
          persons,
          cityId: payloadNumber(row, 'city_id'),
          cityName,
          country,
          ianaTimezone: payloadText(row, 'iana_timezone') || 'Europe/Moscow',
          populationThousands: payloadNumber(row, 'population_thousands'),
          cityPopulationGroup: payloadNumber(row, 'city_population_group'),
          cityLandGroup: payloadNumber(row, 'city_land_group'),
          note,
        })
      } else if (row.event_type === 'ONBOARDING_STUDIO') {
        const partnerNumber = payloadNumber(row, 'partner_number')
        if (!partnerNumber) throw new Error('В кейсе не указан номер франчайзи')
        await planNewStudioForPartner({
          partnerNumber,
          cityId: payloadNumber(row, 'city_id'),
          cityName,
          country,
          ianaTimezone: payloadText(row, 'iana_timezone') || 'Europe/Moscow',
          populationThousands: payloadNumber(row, 'population_thousands'),
          cityPopulationGroup: payloadNumber(row, 'city_population_group'),
          cityLandGroup: payloadNumber(row, 'city_land_group'),
          note,
        })
      } else {
        const studio = row.studios[0]
        if (!studio) throw new Error('В кейсе не указана студия')
        await onboardPartnerStudioPurchase({ persons, studioInternalId: studio.studio_internal_id, note })
      }
      await completeOriginalChangeCase(row.id)
      return true
    }
    if (row.studios.length === 0) {
      throw new Error('В кейсе нет выбранных студий')
    }

    const studiosWithoutNumber = row.studios.filter(studio => studio.studio_number == null)
    const needsStudioNumber = row.event_type !== 'NO_SALES'
      && row.event_type !== 'PURCHASE_PLAN'
      && row.event_type !== 'PURCHASE_COMPLETE'
      && !row.event_type.startsWith('PARTNER_STATUS_')
      && !row.event_type.startsWith('PARTNER_GROUP_')
    if (needsStudioNumber && studiosWithoutNumber.length > 0) {
      throw new Error('Для проведения этого кейса у всех выбранных студий должен быть номер студии')
    }

    const note = conductNote(row)
    const offerPrice = payloadNumber(row, 'offer_price')
    const buyerPartnerNumber = payloadNumber(row, 'buyer_partner_number')
    const partnerNumber = payloadNumber(row, 'partner_number')
    const partnerStatusMode = partnerStatusModeFromEventType(row.event_type)

    for (const studio of row.studios) {
      if (row.event_type === 'NO_SALES') {
        await setStudioServiceStatus({
          studioInternalId: studio.studio_internal_id,
          changedAt: dateAtTen(row.changed_at),
          note,
        })
      } else if (row.event_type === 'SUSPENDED') {
        await suspendStudioFromOriginalCase({
          studioNumber: studio.studio_number ?? 0,
          changedAt: dateAtNoon(row.changed_at),
        })
      } else if (row.event_type === 'CLOSED') {
        await closeStudioFromOriginalCase({
          studioNumber: String(studio.studio_number),
          changedAt: dateAtTwo(row.changed_at),
          closureType: closureTypeFromPayload(row),
        })
      } else if (row.event_type === 'RESUMED') {
        await resumeStudioFromOriginalCase({
          studioNumber: String(studio.studio_number),
          changedAt: dateAtTen(row.changed_at),
        })
      } else if (row.event_type === 'STATE_CHANGE_SALE_OFFER') {
        if (!offerPrice || offerPrice <= 0) throw new Error('В кейсе не указана цена оффера')
        await createStudioSaleOffer({
          studioInternalId: studio.studio_internal_id,
          price: offerPrice,
          currency: payloadText(row, 'offer_currency') || null,
          isBargainAllowed: payloadBoolean(row, 'offer_is_bargain_allowed'),
          changedAt: dateAtTen(row.changed_at),
          note,
        })
      } else if (row.event_type === 'STATE_CHANGE_UPDATE_SALE_OFFER') {
        if (!offerPrice || offerPrice <= 0) throw new Error('В кейсе не указана новая цена оффера')
        await updateStudioSaleOfferFromOriginalCase({
          studioNumber: String(studio.studio_number),
          price: offerPrice,
          currency: payloadText(row, 'offer_currency') || null,
          isBargainAllowed: payloadBoolean(row, 'offer_is_bargain_allowed'),
          changedAt: dateAtTen(row.changed_at),
        })
      } else if (row.event_type === 'SALE_OFFER_CLOSED') {
        await cancelStudioSaleOfferFromOriginalCase({
          studioNumber: String(studio.studio_number),
          changedAt: dateAtTen(row.changed_at),
        })
      } else if (row.event_type === 'STATE_CHANGE_CHANGE_ADDRESS') {
        const newAddress = payloadText(row, 'new_address')
        if (!newAddress.trim()) throw new Error('В кейсе не указан новый адрес')
        await changeStudioAddressFromOriginalCase({
          studioNumber: String(studio.studio_number),
          newAddress,
          changedAt: dateAtTen(row.changed_at),
        })
      } else if (row.event_type === 'STATE_CHANGE_CANCEL_STUDIO') {
        await cancelStudioByIdFromOriginalCase({
          studioInternalId: studio.studio_internal_id,
          changedAt: dateAtTwo(row.changed_at),
        })
      } else if (row.event_type === 'STATE_CHANGE_TRANSFER_TO_MANAGEMENT') {
        await transferStudioToManagementFromOriginalCase({
          studioNumber: String(studio.studio_number),
          changedAt: dateAtTen(row.changed_at),
        })
      } else if (row.event_type === 'ONBOARDING_REGISTER') {
        const address = payloadText(row, 'register_address')
        if (!address.trim()) throw new Error('В кейсе не указан адрес студии')
        await registerPreopenStudio({
          studioInternalId: studio.studio_internal_id,
          address,
          studioNumber: payloadNumber(row, 'register_studio_number'),
          note,
        })
      } else if (row.event_type === 'ONBOARDING_OPEN') {
        if (studio.studio_number == null) throw new Error('В кейсе не указан номер студии')
        await openStudioFromOriginalCase({
          studioNumber: String(studio.studio_number),
          changedAt: dateAtNoon(row.changed_at),
        })
      }
    }

    if (row.event_type === 'PURCHASE_PLAN' || row.event_type === 'PURCHASE_COMPLETE') {
      if (!buyerPartnerNumber) throw new Error('В кейсе не указан франчайзи-покупатель')
      const studio = row.studios[0]
      if (row.studio_count !== 1 || !studio) throw new Error('Покупку можно провести только по одному объекту')
      if (row.event_type === 'PURCHASE_PLAN') {
        await planStudioPurchase({
          studioInternalId: studio.studio_internal_id,
          buyerPartnerNumber,
          note,
        })
      } else {
        await completeStudioPurchase({
          studioInternalId: studio.studio_internal_id,
          buyerPartnerNumber,
          transferDate: row.changed_at,
          sellerExitsNetwork: payloadBoolean(row, 'seller_exits_network'),
          note,
        })
      }
    } else if (partnerStatusMode) {
      if (!partnerNumber) throw new Error('В кейсе не указан номер франчайзи')
      await setPartnerStatus({
        mode: partnerStatusMode,
        targetPartnerNumbers: [partnerNumber],
        changedAt: partnerStatusMode === 'former' ? dateAtThree(row.changed_at) : dateAtNoon(row.changed_at),
      })
    } else if (row.event_type === 'PARTNER_GROUP_ADD_PERSON') {
      if (!partnerNumber) throw new Error('В кейсе не указан номер франчайзи')
      await addPersonToPartnerGroup({
        partnerNumber,
        firstName: payloadText(row, 'person_first_name'),
        lastName: payloadText(row, 'person_last_name'),
        gender: payloadText(row, 'person_gender') === 'м' ? 'м' : 'ж',
        tgUsername: payloadText(row, 'person_tg_username') || null,
        startDate: dateAtNoon(row.changed_at),
      })
    } else if (row.event_type === 'PARTNER_GROUP_END_PERSON') {
      if (!partnerNumber) throw new Error('В кейсе не указан номер франчайзи')
      await endPartnerGroupMembership({
        partnerNumber,
        firstName: payloadText(row, 'person_first_name'),
        lastName: payloadText(row, 'person_last_name'),
        endDate: row.changed_at,
      })
    } else if (row.event_type === 'PARTNER_GROUP_DISPLAY_NAME') {
      const targetPersonId = payloadNumber(row, 'person_id')
      if (!targetPersonId) throw new Error('В кейсе не указан ID персоны')
      await applyDisplayNameChange({
        personId: targetPersonId,
        displayName: payloadText(row, 'display_name') || null,
        note,
      })
    }

    await completeOriginalChangeCase(row.id)
    return true
  }

  const handleConduct = async () => {
    if (!selectedCase) return
    const action = getConductAction(selectedCase.event_type)
    const canConductDirectly = isDirectStructuredConductCase(selectedCase)
    if (!action && !canConductDirectly) {
      setActionError('Для этого типа кейса пока нет связанной формы проведения.')
      return
    }
    if (selectedCase.is_bulk) {
      const confirmed = window.confirm(
        `Изменение будет применено ко всем ${selectedCase.studio_count} выбранным студиям.\n\nТип: ${selectedCase.event_label}\nЦелевой статус: ${statusLabel(selectedCase.target_status)}\nДата события: ${formatDate(selectedCase.changed_at)}`
      )
      if (!confirmed) return
    }

    if (canConductDirectly) {
      setConductSubmittingId(selectedCase.id)
      setActionError(null)
      try {
        await applyDirectStructuredCase(selectedCase)
        await loadRows()
      } catch (conductError) {
        setActionError(getErrorMessage(conductError))
        await loadRows()
      } finally {
        setConductSubmittingId(null)
      }
      return
    }

    if (!action) return
    setCaseCompletingAfterConduct(selectedCase.id)
    setConductAction(action)
  }

  const completeAfterConduct = async () => {
    const caseId = caseCompletingAfterConduct
    setConductAction(null)
    setCaseCompletingAfterConduct(null)
    if (!caseId) return

    setActionError(null)
    try {
      await completeOriginalChangeCase(caseId)
      await loadRows()
    } catch (completeError) {
      setActionError(getErrorMessage(completeError))
      await loadRows()
    }
  }

  return (
    <div className="min-h-[calc(100vh-112px)] bg-slate-50 pb-24">
      {conductAction?.kind === 'state_change' && (
        <StudioStateChangeModal
          initialMode={conductAction.mode}
          onClose={() => setConductAction(null)}
          onDone={completeAfterConduct}
        />
      )}
      {conductAction?.kind === 'purchase' && (
        <StudioPurchaseModal
          initialMode={conductAction.mode}
          onClose={() => setConductAction(null)}
          onDone={completeAfterConduct}
        />
      )}
      {conductAction?.kind === 'onboarding' && (
        <StudioOnboardingModal
          initialMode={conductAction.mode}
          initialPartnerTarget={conductAction.partnerTarget}
          initialData={{
            payload: selectedCase.payload,
            studioInternalId: selectedCase.studios[0]?.studio_internal_id ?? null,
            changedAt: selectedCase.changed_at,
          }}
          onClose={() => setConductAction(null)}
          onDone={completeAfterConduct}
        />
      )}
      {conductAction?.kind === 'partner_status' && (
        <PartnerStatusModal
          initialMode={conductAction.mode}
          onClose={() => setConductAction(null)}
          onDone={completeAfterConduct}
        />
      )}
      {conductAction?.kind === 'partner_group' && (
        <PartnerGroupMembershipModal
          initialMode={conductAction.mode}
          onClose={() => setConductAction(null)}
          onDone={completeAfterConduct}
        />
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ClipboardList size={20} className="text-amber-600" />
            <h1 className="text-lg font-semibold text-slate-900">Кейсы</h1>
          </div>
          <button
            type="button"
            onClick={() => loadRows()}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-card px-3 text-sm text-slate-700 hover:bg-slate-100"
          >
            <RefreshCw size={15} />
            Обновить
          </button>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle size={17} className="mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
          <aside className="h-fit border border-slate-200 bg-card p-2">
            <div className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Кабинет кейсов</div>
            <nav className="space-y-1">
              {TABS.map(tab => {
                const Icon = tab.icon
                const count = statusCounts[tab.value]
                const isActive = activeTab === tab.value
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => changeTab(tab.value)}
                    className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors ${
                      isActive ? 'bg-amber-50 text-slate-950 ring-1 ring-amber-200' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950'
                    }`}
                  >
                    <Icon size={16} className={isActive ? 'text-amber-600' : 'text-slate-400'} />
                    <span className="min-w-0 flex-1 truncate">{tab.label}</span>
                    <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${
                      isActive ? 'bg-amber-300 text-slate-950' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {count}
                    </span>
                  </button>
                )
              })}
            </nav>
          </aside>

          <div className="overflow-hidden border border-slate-200 bg-card">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">{TABS.find(tab => tab.value === activeTab)?.label}</div>
                <div className="text-xs text-slate-500">Показано {visibleRows.length.toLocaleString('ru-RU')} из {rows.length.toLocaleString('ru-RU')}</div>
              </div>
            </div>
            <div className="max-h-[620px] overflow-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="w-24 px-3 py-2 font-semibold">№ кейса</th>
                    <th className="min-w-64 px-3 py-2 font-semibold">Объект</th>
                    <th className="min-w-64 px-3 py-2 font-semibold">Передано</th>
                    <th className="w-36 px-3 py-2 font-semibold">Дата события</th>
                    <th className="w-44 px-3 py-2 font-semibold">Кто передал</th>
                    <th className="w-40 px-3 py-2 font-semibold">Кейс создан</th>
                    <th className="w-32 px-3 py-2 font-semibold">Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-12 text-center text-slate-500">
                        <Loader2 size={20} className="mx-auto mb-2 animate-spin" />
                        Загружаем кейсы
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-12 text-center">
                        <div className="mx-auto max-w-xs rounded-md border border-slate-200 bg-slate-50 px-4 py-5">
                          <ListChecks size={22} className="mx-auto mb-2 text-slate-400" />
                          <div className="text-sm font-medium text-slate-900">Кейсов пока нет</div>
                          <div className="mt-1 text-xs text-slate-500">Переданные из «Исходного» кейсы появятся здесь.</div>
                        </div>
                      </td>
                    </tr>
                  ) : visibleRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-12 text-center text-sm text-slate-500">В этом разделе кейсов нет</td>
                    </tr>
                  ) : (
                    visibleRows.map(row => {
                      const change = changeParts(row)
                      return (
                        <tr
                          key={row.id}
                          onClick={() => openCase(row)}
                          className={`cursor-pointer border-b border-slate-100 hover:bg-amber-50/50 ${
                            selectedId === row.id ? 'bg-amber-50' : ''
                          }`}
                        >
                          <td className="px-3 py-3 align-top font-semibold text-slate-900">#{row.id}</td>
                          <td className="px-3 py-3 align-top text-slate-900">
                            <div className="font-semibold">{objectLabel(row)}</div>
                            <div className="mt-0.5 text-xs text-slate-500">{partnerLine(row)}</div>
                          </td>
                          <td className="px-3 py-3 align-top">
                            <div className="flex min-w-0 items-center gap-2 font-semibold text-slate-950">
                              <span className="truncate text-slate-600">{change.from}</span>
                              <ArrowRight size={15} className="shrink-0 text-amber-600" />
                              <span className="truncate text-amber-700">{change.to}</span>
                            </div>
                            <div className="mt-0.5 text-xs text-slate-500">{row.event_label}</div>
                          </td>
                          <td className="px-3 py-3 align-top text-slate-700">
                            <EventDateValue value={row.changed_at} />
                          </td>
                          <td className="px-3 py-3 align-top text-slate-700">{row.created_by_name || '—'}</td>
                          <td className="px-3 py-3 align-top text-slate-700">{formatDateTime(row.created_at)}</td>
                          <td className="px-3 py-3 align-top">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${statusBadgeClass(row.status)}`}>
                              {STATUS_LABELS[row.status] ?? row.status}
                            </span>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {selectedCase && (
        <CaseDrawer
          row={selectedCase}
          onClose={closeCase}
          onReject={() => {
            setActionError(null)
            setRejectDialogOpen(true)
          }}
          onEdit={() => {
            setActionError(null)
            setEditDialogOpen(true)
          }}
          onConduct={() => void handleConduct()}
          actionError={actionError}
          conducting={conductSubmittingId === selectedCase.id}
        />
      )}

      {editDialogOpen && selectedCase && (
        <EditCaseScopeDialog
          row={selectedCase}
          onClose={() => setEditDialogOpen(false)}
          onSaved={async () => {
            setEditDialogOpen(false)
            await loadRows()
          }}
        />
      )}

      {rejectDialogOpen && selectedCase && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-md border border-slate-200 bg-card p-4 shadow-xl">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">Отклонить кейс #{selectedCase.id}</div>
                <div className="mt-0.5 text-sm text-slate-500">Причина обязательна</div>
              </div>
              <button
                type="button"
                onClick={() => setRejectDialogOpen(false)}
                disabled={rejectSubmitting}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X size={16} />
              </button>
            </div>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Причина</span>
              <select
                value={rejectionReason}
                onChange={event => setRejectionReason(event.target.value)}
                className="h-10 w-full rounded-md border border-slate-200 bg-card px-3 text-sm outline-none focus:ring-1 focus:ring-amber-400"
              >
                {REJECTION_REASONS.map(reason => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </label>
            <label className="mt-3 block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Комментарий</span>
              <textarea
                value={rejectionComment}
                onChange={event => setRejectionComment(event.target.value)}
                rows={3}
                className="w-full rounded-md border border-slate-200 bg-card px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-amber-400"
              />
            </label>
            {actionError && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle size={17} className="mt-0.5 shrink-0" />
                <div>{actionError}</div>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRejectDialogOpen(false)}
                disabled={rejectSubmitting}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={rejectSubmitting}
                className="inline-flex items-center gap-2 rounded-md bg-fixed-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:bg-red-300"
              >
                <CheckCircle2 size={15} />
                {rejectSubmitting ? 'Отклоняем...' : 'Отклонить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
